import { createRouter, createWebHistory } from 'vue-router'
import { authUtils } from '@/api/auth'
import { authStateManager } from '@/api/auth'
import { tokenRefreshManager } from '@/main'
import { clearAllAuthState, isTokenExpired as utilsIsTokenExpired } from '@/utils/authUtils'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/Login.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/',
      name: 'home',
      component: () => import('@/views/Home.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/menu',
      name: 'menu',
      component: () => import('@/views/MenuManagement.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/role',
      name: 'role',
      component: () => import('@/views/RoleManagement.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/user',
      name: 'user',
      component: () => import('@/views/UserManagement.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/business-menu',
      name: 'business-menu',
      component: () => import('@/views/BusinessMenuManagement.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/business-function',
      name: 'business-function',
      component: () => import('@/views/BusinessFunctionManagement.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/brand',
      name: 'brand',
      component: () => import('@/views/BrandManagement.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/upload-demo',
      name: '文件上传-demo',
      component: () => import('@/views/FileUploadDemo.vue'),
      meta: { requiresAuth: true }
    }
  ]
})

// 路由守卫
router.beforeEach(async (to, from, next) => {
  // 检查是否需要认证
  const requiresAuth = to.meta.requiresAuth !== false // 默认为需要认证
  
  // 获取认证状态 - 直接检查localStorage
  let token = localStorage.getItem('platformToken')
  const currentUser = localStorage.getItem('currentUser')
  const refreshToken = localStorage.getItem('platformToken_refresh')
  
  // 如果token不存在或已过期，但有refreshToken，尝试刷新
  if ((!token || utilsIsTokenExpired(token)) && refreshToken) {
    try {
      // 如果正在刷新，等待现有刷新完成
      if (tokenRefreshManager.isRefreshing() && tokenRefreshManager.getRefreshPromise()) {
        const newToken = await tokenRefreshManager.getRefreshPromise()
        if (newToken) {
          token = newToken
        } else {
          clearAllTokens()
        }
      } else {
        // 执行新的刷新
        const newToken = await refreshTokenInRouter(refreshToken)
        if (newToken) {
          token = newToken
        } else {
          clearAllTokens()
        }
      }
    } catch (error) {
      clearAllTokens()
    }
  }
  
  const isLoggedIn = !!(token && currentUser)

  // 如果访问登录页
  if (to.path === '/login') {
    if (isLoggedIn) {
      next('/dashboard')
    } else {
      next()
    }
    return
  }

  // 如果需要认证但未登录
  if (requiresAuth && !isLoggedIn) {
    // 保存原始目标路径，登录后可以跳转回去
    if (to.path !== '/login') {
      sessionStorage.setItem('redirectAfterLogin', to.fullPath)
    }
    next('/login')
    return
  }

  // 只有在首次访问根路径且已登录时，才尝试刷新用户信息
  if (to.path === '/' && isLoggedIn && from.path === '/login') {
    try {
      await authUtils.refreshUserInfo()
    } catch (error) {
      console.error('❌ 刷新用户信息失败:', error)
      // 只有在用户信息刷新失败且没有有效token时才清除认证状态
      if (!token || utilsIsTokenExpired(token)) {
        authStateManager.clearAuthState()
        sessionStorage.setItem('redirectAfterLogin', to.fullPath)
        next('/login')
        return
      }
    }
  }
  next()
})



// 在路由守卫中刷新token
async function refreshTokenInRouter(refreshToken: string): Promise<string | null> {
  try {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5002'
    const apiUrl = apiBaseUrl.endsWith('/') 
      ? `${apiBaseUrl}api/auth/refresh`
      : `${apiBaseUrl}/api/auth/refresh`
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken })
    })
    
    const data = await response.json()
    
    if (data.success && data.data) {
      const newAccessToken = data.data
      localStorage.setItem('platformToken', newAccessToken)
      return newAccessToken
    } else {
      // 刷新失败时清除所有认证状态
      clearAllTokens()
      return null
    }
  } catch (error) {
    console.error('❌ 路由守卫：Token刷新过程中出错:', error)
    // 刷新过程中出错时清除所有认证状态
    clearAllTokens()
    return null
  }
}

// 清除所有token和认证状态
function clearAllTokens(): void {
  clearAllAuthState('路由守卫')
}

export default router
